import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { useApi, errMsg, money } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUSES = { estimates: ["draft", "sent", "approved", "declined"], invoices: ["draft", "sent", "paid", "overdue"] };
const blank = () => ({ project_id: "", customer_name: "", customer_email: "", title: "", items: [{ description: "", quantity: 1, unit_price: 0 }], tax_rate: 0, notes: "", status: "draft", due_date: "" });

const ItemRow = ({ item, i, onChange, onRemove }) => (
  <div className="grid grid-cols-12 gap-2">
    <Input className="col-span-12 sm:col-span-6" placeholder="Description (e.g. 4in slab, 600 sq ft)" value={item.description} onChange={(e) => onChange(i, "description", e.target.value)} data-testid={`item-desc-${i}`} />
    <Input className="col-span-4 sm:col-span-2" type="number" step="0.01" min="0" value={item.quantity} onChange={(e) => onChange(i, "quantity", e.target.value)} data-testid={`item-qty-${i}`} />
    <Input className="col-span-5 sm:col-span-3" type="number" step="0.01" min="0" value={item.unit_price} onChange={(e) => onChange(i, "unit_price", e.target.value)} data-testid={`item-price-${i}`} />
    <button type="button" onClick={() => onRemove(i)} className="col-span-3 grid place-items-center rounded-md border sm:col-span-1" data-testid={`item-remove-${i}`}><Trash2 size={14} /></button>
  </div>
);

export const DocEditor = ({ kind, open, onOpenChange, doc, onSaved }) => {
  const api = useApi();
  const [f, setF] = useState(blank());
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    if (!open) return;
    setF(doc ? { ...blank(), ...doc } : blank());
    api("get", "/projects").then(setProjects);
  }, [open, doc, api]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setItem = (i, k, v) => setF({ ...f, items: f.items.map((it, j) => (j === i ? { ...it, [k]: v } : it)) });
  const pickProject = (pid) => {
    const p = projects.find((x) => x.id === pid);
    setF({ ...f, project_id: pid === "none" ? "" : pid, customer_name: p?.customer_name || f.customer_name, customer_email: p?.customer_email || f.customer_email });
  };
  const subtotal = f.items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0), 0);
  const tax = subtotal * (Number(f.tax_rate) || 0) / 100;

  const save = async (e) => {
    e.preventDefault();
    const body = { ...f, tax_rate: Number(f.tax_rate) || 0, items: f.items.filter((i) => i.description).map((i) => ({ description: i.description, quantity: Number(i.quantity) || 0, unit_price: Number(i.unit_price) || 0 })) };
    try {
      const saved = doc ? await api("put", `/${kind}/${doc.id}`, body) : await api("post", `/${kind}`, body);
      toast.success("Saved");
      onSaved(saved);
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader><DialogTitle className="font-display">{doc ? `Edit ${doc.number}` : `New ${kind === "estimates" ? "estimate" : "invoice"}`}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4" data-testid="doc-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5"><Label className="text-xs">Project</Label>
              <Select value={f.project_id || "none"} onValueChange={pickProject}>
                <SelectTrigger data-testid="doc-project-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">No project</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Title</Label><Input value={f.title} onChange={set("title")} placeholder="e.g. Driveway & patio" data-testid="doc-title-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Customer name</Label><Input value={f.customer_name} onChange={set("customer_name")} data-testid="doc-customer-name-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Customer email (shows in their portal)</Label><Input type="email" required value={f.customer_email} onChange={set("customer_email")} data-testid="doc-customer-email-input" /></div>
          </div>
          <div className="space-y-2">
            <div className="hidden grid-cols-12 gap-2 text-xs font-semibold text-[#8C877D] sm:grid"><span className="col-span-6">Item</span><span className="col-span-2">Qty</span><span className="col-span-3">Unit price</span></div>
            {f.items.map((it, i) => <ItemRow key={i} item={it} i={i} onChange={setItem} onRemove={(j) => setF({ ...f, items: f.items.filter((_, k) => k !== j) })} />)}
            <button type="button" onClick={() => setF({ ...f, items: [...f.items, { description: "", quantity: 1, unit_price: 0 }] })} className="inline-flex items-center gap-1 text-sm font-semibold text-[#A67C00]" data-testid="add-item-btn"><Plus size={14} />Add line item</button>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5"><Label className="text-xs">Tax rate %</Label><Input type="number" step="0.01" min="0" value={f.tax_rate} onChange={set("tax_rate")} data-testid="doc-tax-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Status</Label>
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                <SelectTrigger data-testid="doc-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES[kind].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {kind === "invoices" && <div className="space-y-1.5"><Label className="text-xs">Due date</Label><Input type="date" value={f.due_date} onChange={set("due_date")} data-testid="doc-due-input" /></div>}
          </div>
          <Textarea placeholder="Notes / terms" value={f.notes} onChange={set("notes")} data-testid="doc-notes-input" />
          <div className="flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="font-mono-num text-sm">Subtotal {money(subtotal)} · Tax {money(tax)} · <b data-testid="doc-total">Total {money(subtotal + tax)}</b></div>
            <button className="rounded-full bg-[#F2B91C] px-6 py-2.5 text-sm font-semibold text-[#0E1F3F] hover:bg-[#D9A30F]" data-testid="doc-save-btn">Save</button>
          </div>
          <p className="text-xs text-[#8C877D]">Customers only see it once the status is "sent" or later.</p>
        </form>
      </DialogContent>
    </Dialog>
  );
};
